import { CSSProperties, HTMLAttributes } from 'react';
import { sva, cx, css } from '@mildang/styled-system/css';
import { CircularProgressProps } from './type';
import { type Token, token } from '@mildang/styled-system/tokens';
import { migrateSize } from '../../../lib/migrateSize';
const SIZE = 44;
// Figma 스펙: 스피너 stroke 외곽 ~ 컨테이너 사이 여백 = 컨테이너 사이즈의 8.33% (=1/12).
// 렌더 사이즈별 결과값: 18→1.5, 20→1.67, 22→1.83, 24→2, 28→2.33, 32→2.67, 48→4 (px).
const INNER_PADDING_RATIO = 1 / 12;

const style = sva({
  slots: ['root', 'svg', 'circle'],
  base: {
    root: {
      display: 'inline-flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexShrink: 0,
      position: 'relative',
    },
    svg: {
      width: '[inherit]',
      height: '[inherit]',
      display: '[inherit]',
    },
    circle: {
      transformOrigin: '0px 0px',
      stroke: '[var(--circle-fill-color)]',
      _highContrast: {
        stroke: '[Highlight]',
      },
    },
  },
  variants: {
    variant: {
      determinate: {},
      indeterminate: {
        root: {
          animation: 'circularRotate  1.4s linear infinite',
        },
        circle: {
          animation: 'circularDash  1.4s ease-in-out infinite',
          strokeDasharray: '80px, 200px',
          strokeDashoffset: 0,
        },
      },
    },
  },
});

const spinnerSize = {
  '2xs': 18,
  xs: 20,
  sm: 22,
  md: 24,
  lg: 28,
  xl: 32,
  '2xl': 48,
} as const;

export function CircularProgress(props: CircularProgressProps) {
  const {
    className,
    color = 'positive.fill.base',
    size: sizeProp = 'xl',
    thickness = 3.6,
    value = 0,
    variant = 'indeterminate',
    shape = 'butt',
    panda = {},
    ...other
  } = props;
  const classes = style({ variant });

  const migrated = typeof sizeProp === 'number' ? sizeProp : migrateSize(sizeProp);
  const size = typeof migrated === 'number' ? migrated : spinnerSize[migrated as keyof typeof spinnerSize];

  const circleStyle: CSSProperties = {};
  const rootStyle: CSSProperties = {};
  const rootProps: HTMLAttributes<HTMLSpanElement> = {};

  if (variant === 'determinate') {
    const circumference = 2 * Math.PI * ((SIZE - thickness) / 2);

    rootProps['aria-valuemin'] = 0;
    rootProps['aria-valuemax'] = 100;
    rootProps['aria-valuenow'] = Math.round(value);
    rootStyle.transform = 'rotate(-90deg)';

    circleStyle.strokeDashoffset = `${(((100 - value) / 100) * circumference).toFixed(3)}px`;
    circleStyle.strokeDasharray = circumference.toFixed(3);
  }

  const resolvedColor = color === 'currentColor' ? 'currentColor' : token(`colors.${color}` as Token);

  // 뷰박스(44) 기준 innerPadding — 렌더 시 스케일되어 결과 여백 = renderedSize * RATIO 가 된다.
  const innerPadding = Math.max(1, SIZE * INNER_PADDING_RATIO);

  return (
    <span
      role="progressbar"
      className={cx(classes.root, css({}, panda), className)}
      style={{
        width: size,
        height: size,
        ...rootStyle,
      }}
      {...rootProps}
      {...other}
    >
      <svg
        className={classes.svg}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        style={
          {
            ['--circle-fill-color']: resolvedColor,
          } as CSSProperties
        }
      >
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={Math.max(0, (SIZE - thickness) / 2 - innerPadding)}
          strokeWidth={thickness}
          fill="none"
          className={classes.circle}
          style={circleStyle}
          strokeLinecap={shape}
        />
      </svg>
    </span>
  );
}
